/**
 * PlayerManager - Player profile and scoring management
 * Handles player data, statistics, and game results
 * Based on ADR-003: Game State Management Architecture
 */

import type { PlayerState, PlayerProfile, GameResult, Player } from '@/types';
import { StorageService } from './StorageService';

/**
 * Creates default player profile
 */
function createDefaultPlayerProfile(name: string): PlayerProfile {
  return {
    name,
    wins: 0,
    losses: 0,
    draws: 0,
  };
}

/**
 * Creates initial player state
 */
function createInitialPlayerState(): PlayerState {
  return {
    playerX: createDefaultPlayerProfile('Player X'),
    playerO: createDefaultPlayerProfile('Player O'),
    gameHistory: [],
  };
}

/**
 * PlayerManager
 * Manages player profiles, scores, and persistence
 */
export class PlayerManager {
  private playerState: PlayerState;
  private storageService: StorageService;

  constructor(storageService: StorageService) {
    this.storageService = storageService;
    
    // Load existing player state or create new
    const savedState = this.storageService.loadPlayerState();
    this.playerState = savedState ?? createInitialPlayerState();
  }

  /**
   * Get current player state
   */
  public getPlayerState(): Readonly<PlayerState> {
    return Object.freeze({ ...this.playerState });
  }

  /**
   * Update player name
   */
  public updatePlayerName(player: Player, name: string): void {
    const sanitizedName = name.trim().slice(0, 20) || `Player ${player}`;
    
    if (player === 'X') {
      this.playerState.playerX.name = sanitizedName;
    } else {
      this.playerState.playerO.name = sanitizedName;
    }
    
    this.save();
  }

  /**
   * Record game result and update scores
   */
  public recordGameResult(result: GameResult): void {
    // Update scores
    if (result.winner === 'draw') {
      this.playerState.playerX.draws++;
      this.playerState.playerO.draws++;
    } else if (result.winner === 'X') {
      this.playerState.playerX.wins++;
      this.playerState.playerO.losses++;
    } else {
      this.playerState.playerO.wins++;
      this.playerState.playerX.losses++;
    }

    // Add to history
    this.playerState.gameHistory.unshift(result);

    this.save();
  }

  /**
   * Reset all scores to zero
   */
  public resetScores(): void {
    this.playerState.playerX = {
      ...this.playerState.playerX,
      wins: 0,
      losses: 0,
      draws: 0,
    };
    
    this.playerState.playerO = {
      ...this.playerState.playerO,
      wins: 0,
      losses: 0,
      draws: 0,
    };

    this.playerState.gameHistory = [];

    this.save();
  }

  /**
   * Get player profile by player type
   */
  public getPlayerProfile(player: Player): Readonly<PlayerProfile> {
    return Object.freeze(
      player === 'X' ? { ...this.playerState.playerX } : { ...this.playerState.playerO }
    );
  }

  /**
   * Save player state to storage
   */
  private save(): void {
    this.storageService.savePlayerState(this.playerState);
  }
}
